/* eslint-disable object-shorthand */
import { createSelector } from '@reduxjs/toolkit';
import { Car } from '@lib/azgaz-ui/connect';
import { getSpecification } from '@azgaz/azgaz-ui';
import { selectors as carSelectors } from '@/apps/appCreator/store/car';
import { State } from '../apps/appCreator/store';
import getCatalogVersionByCar from './getCatalogVersionByCar';

/**
 * Селектор в котором осуществляется разрешение автомоля со специфичными для данного бренда (виджета) расчетами
 */
const getBrandCar = createSelector(
  getCatalogVersionByCar,
  (state: State, car: Car | undefined) => car,
  carSelectors.selectDiscounts,
  carSelectors.selectTradeInCar,
  (version, car, carSelectableDiscounts, tradeInCar) => {
    if (!car) return undefined;

    // Выгода дилера
    const dealerDiscount: number | undefined = getSpecification(
      car.specification,
      'other',
      'dealer_discount'
    );
    const isTradeInCarSuzuki = tradeInCar.brand === 'SUZUKI';

    const {
      suzukiFinanceDiscount,
      anotherBrandSuzukiFinanceDiscount,
      tradeInDiscount = 0,
      anotherBrandTradeInDiscount = 0,
      medicalDiscount,
    } = version || {};

    const price = car.price?.value?.[0][0] as number;
    const dealerPrice = car.price?.value?.[0][1] as number;

    // Общие выгоды
    const summOfDiscounts =
      (suzukiFinanceDiscount || 0) +
      (tradeInDiscount || 0) +
      (medicalDiscount || 0);

    // стоимоcть авто trade-in
    let tradeInPrice = 0;

    // Общие выгоды (выбранные пользователем на странице авто)
    const summOfSelectedDiscounts = Object.keys(carSelectableDiscounts).reduce(
      (acc, key) => {
        const discount = carSelectableDiscounts[key];
        const priceValue = discount.selected ? discount.value : 0;

        if (key === 'tradeInPrice') {
          tradeInPrice = priceValue;
        }

        return acc + priceValue;
      },
      0
    );

    // Цена с учетом выбранных выгод
    const carPriceDiscounted = price - summOfSelectedDiscounts;

    // Цена с учетом выбранных выгод
    const dealerCarPriceDiscounted = dealerPrice - summOfSelectedDiscounts;

    // Цена с учетом выбранных выгод, без стоимсоти авто trade-in
    const summOfSelectedDiscountsWithoutTradeInPrice =
      summOfSelectedDiscounts - tradeInPrice;

    // Выгоды дилера
    const dealerSummOfDiscounts = summOfDiscounts + (dealerDiscount || 0);

    const discountPrice =
      price - summOfDiscounts > 0 ? price - summOfDiscounts : 0;

    const dealerDiscountPrice =
      dealerPrice - dealerSummOfDiscounts > 0
        ? dealerPrice - dealerSummOfDiscounts
        : 0;

    return {
      ...car,
      extra: {
        price, // Цена
        discountPrice, // Цена со всеми возможными выгодами
        dealerPrice, // Дилерская цена
        dealerDiscountPrice, // Дилерская цена со всеми возможными выгодами
        dealerDiscount, // Выгода дилера
        carPriceDiscounted: carPriceDiscounted < 0 ? 0 : carPriceDiscounted, // Цена с учетом выбранных выгод
        dealerCarPriceDiscounted:
          dealerCarPriceDiscounted < 0 ? 0 : dealerCarPriceDiscounted, // Цена дилера с учетом выбранных выгод
        carPriceDiscountedWithoutTradeInPrice:
          carPriceDiscounted + tradeInPrice, // Цена с учетом выбранных выгод исключая стоимоcть авто trade-in
        dealerCarPriceDiscountedWithoutTradeInPrice:
          dealerCarPriceDiscounted + tradeInPrice, // Цена дилера с учетом выбранных выгод исключая стоимоcть авто trade-in
        suzukiFinanceDiscount,
        anotherBrandSuzukiFinanceDiscount,
        resolvedSuzukiFinanceDiscount: isTradeInCarSuzuki
          ? suzukiFinanceDiscount
          : anotherBrandSuzukiFinanceDiscount,
        tradeInPrice: carSelectableDiscounts.tradeInPrice.value, // расчетная стоимоить трейд-ин
        tradeInDiscount,
        anotherBrandTradeInDiscount,
        resolvedTradeInDiscount: isTradeInCarSuzuki
          ? tradeInDiscount
          : anotherBrandTradeInDiscount,
        medicalDiscount,
        summOfDiscounts, // Общий размер всех возможных выгод
        summOfSelectedDiscounts: summOfSelectedDiscounts, // Общий размер всех выбранных выгод
        summOfSelectedDiscountsWithoutTradeInPrice: summOfSelectedDiscountsWithoutTradeInPrice,
        hasDiscounts: summOfDiscounts > 0 && discountPrice > 0, // Имеется хотя бы одна выгода
        hasDealerDiscounts:
          dealerSummOfDiscounts > 0 && dealerDiscountPrice > 0, // Имеется хотя бы одна выгода у дилера
      },
    };
  }
);

// FIX: исправляем тут тип т.к. неверно отрабатывает type inference (из car отбрасывается { [K: string]: any })
export default getBrandCar as (state: State, car: Car | undefined) => TBrandCar;

export type TBrandCar = (
  | (NonNullable<ReturnType<typeof getBrandCar>> & {
      extra: { [K: string]: any };
      [K: string]: any;
    })
  | undefined
) & {
  group?: 'new' | 'used';
};
